// 클레임 등록(단건 · 구성안 화면①) — 왼쪽 ① 송장번호로 주문 조회 · ② 클레임 내용 입력 · ③ 금액·접수기한
// · 오른쪽 ④ AI 자동 분류 제안 · ⑤ 접수 기준 체크 · 승인 담당자 · [등록(승인)] [임시저장] [임시저장 삭제] · 아래 오늘 등록한 클레임.
// 서버 routers/claim_ledger.py — 조회 GET lookup · 계산 POST preview(쓰지 않는다) · 저장 POST/PUT(action draft|register) · 사진 multipart.
// 🔴 금액·기한·승인 필요 여부는 서버 미리보기만 셈한다 — 입력이 바뀌면 300ms 뒤 preview 를 부르고 마지막 요청의 응답만 쓴다.
// 🔴 송장은 숫자만 보낸다(8~20자 아니면 요청 없이 칸 옆 오류) · 출고정보가 없으면 «출고정보 없음 — 직접 입력하세요» 로 수기 입력.
// 🔴 저장 순서: 저장 → 주소 ?id= 반영 → 사진을 한 장씩 업로드(실패한 사진만 남긴다) → 원장 다시 읽기 → 오늘 목록 다시 읽기.
// 🔴 ?id= 로 열면 임시저장은 편집, 그 밖 상태는 읽기 전용. 처리 불가(blocked)·필수 누락이면 [등록(승인)] 이 비활성이고 이유를 보인다.
// 🔴 DELETE 요청은 없다 — 임시저장 삭제 = discard(확인 대화상자) · 사진 지우기 = removePhoto.
// 🔴 자동 배지(「자동」「자동계산」「자동지정」)는 값 유무와 상관없이 늘 그린다 — 조회로 채운 원값(autoOrigin)과 칸 값이 다르면 「수정됨」.
// 🔴 [엑셀 일괄등록] = /claims/register/bulk(ClaimBulkPage) · ?bulk_upload=&bulk_row= 로 열면(일괄등록 오류 행) 그 행 값으로 채운다
//    — 조회(lookup)를 다시 부르지 않고(저장한 스냅샷) 원장은 없다(claim null · 저장하면 새 원장).
// 🔴 클레임 [불러오기](2.0·1.0) = applyLoadedClaim — 서버 lines 의 줄만 체크하고 나머지 조회 줄은 해제한다 · 서버 notices 를 ② 위에 보인다.
//    재출고 송장이면(lookup.reship_of) 원 클레임 안내 + [원주문 송장으로 조회] · 클레임을 안 불러오고 저장하면 원 클레임 연결이 남는다.
// 🔴 면책(귀책 고객사)·금액 0원은 서버 미리보기(calc.exempt)만 셈한다 — 화면은 그리기만 한다.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconFileInvoice } from '@tabler/icons-react';
import AppShell from '../components/AppShell';
import { Page, PageHeader, Button } from '../components/ui';
import { claimBulkAPI, claimLedgerAPI } from '../api/client';
import { confirmDialog, toast } from '../components/AppDialog';
import { fmtKstShort } from '../utils/kstTime';
import { COLORS, badge, card, input } from '../utils/uiStyles';
import { errorText, fmtWon, labelOf, statusOf } from './claims/claimLabels';
import {
  EMPTY_DRAFT, amountMissingText, applyLoadedClaim, autoOriginFrom, blankRow, buildBody, checkInvoice, claimKindText,
  claimRefFromClaim, claimRefText, columnEdited, courierDeadlineText, groupRowsByOrder, intOrNull, isEdited,
  optionsWithCurrent, packSummaryText, productBasisText, registerBlockers, rowsFromClaim, rowsFromLookup,
  shipperDeadlineText,
} from './claims/claimRegisterUtil';
import { prefillFromBulkRow } from './claims/claimBulkUtil';

const PREVIEW_DELAY = 300;
const NO_SHIPMENT = '출고정보 없음 — 직접 입력하세요';
const V1_QTY_NOTE = '1.0 출고는 1행=1EA 로 보았습니다(수량을 확인하세요)';
const TYPES_UNSET_NOTE = '2.0 클레임 유형 설정(교환·반품·취소 값)이 비어 있어 유형을 구분하지 못했습니다 — 보상기준 마스터 설정에서 넣으세요';
const SOURCE_BADGE = { v2: '2.0', v1: '1.0' };
const STATUS_LABEL = {
  draft: '임시저장', registered: '등록', pending_approval: '승인 대기', approved: '승인', rejected: '반려', cancelled: '취소',
};
const STATUS_TONE = { draft: 'gray', registered: 'blue', pending_approval: 'orange', approved: 'green', rejected: 'red', cancelled: 'gray' };
const ZONE_OPTIONS = [
  { key: 'ambient', label: '상온' }, { key: 'cold', label: '냉장·냉동' }, { key: 'chilled', label: '냉장' }, { key: 'frozen', label: '냉동' },
];
const ZONE_SOURCE = { shipper_standard: '고객사 기준', roster: '고객사 명부', product: '상품 보관온도' };
const HANDLING_OPTIONS = ['reship', 'refund'];
const FAULT_OPTIONS = ['center', 'courier', 'shipper'];
const ACCIDENTS = [{ key: 'courier', label: '택배사고' }, { key: 'general', label: '일반사고' }];
// 클레임 항목 분류 배지 색(서버 category) — 화주·고객 사유 주황 · 사고 후보 파랑 · 판단 필요 회색.
const CATEGORY_TONE = { shipper_customer: 'orange', accident: 'blue', review: 'gray' };
const CLAIM_LIST = {
  v2: { title: '2.0 클레임', empty: '이 주문의 2.0 클레임이 없습니다.', key: 'v2_claim_no', row: 'claim-v2-row', reship: 'claim-v2-reship' },
  v1: { title: '1.0 클레임', empty: '이 주문의 1.0 클레임이 없습니다.', key: 'v1_claim_key', row: 'claim-v1-row', reship: 'claim-v1-reship' },
};

const sectionCard = { ...card, marginTop: '14px' };
const sectionTitle = { margin: '0 0 10px', fontSize: '15px', fontWeight: 600, color: COLORS.text };
const fieldBox = { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: COLORS.textSub, minWidth: '150px', flex: '1 1 150px' };
const grid = { display: 'flex', flexWrap: 'wrap', gap: '10px 14px', alignItems: 'flex-end' };
const th = { padding: '6px 8px', textAlign: 'left', fontSize: '12px', fontWeight: 400, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' };
const td = { padding: '6px 8px', fontSize: '13px', color: COLORS.text, borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'middle' };
const errText = { color: COLORS.dangerText, fontSize: '12px' };
const note = { fontSize: '12px', color: COLORS.textSub, margin: '6px 0 0' };
const autoBadge = { ...badge('blue'), fontSize: '11px', padding: '1px 6px', marginLeft: '6px' };
const editedBadge = { ...badge('orange'), fontSize: '11px', padding: '1px 6px', marginLeft: '6px' };
const smallInput = { ...input, padding: '5px 8px', width: '96px' };
const amountRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px', padding: '6px 0', borderBottom: `1px solid ${COLORS.borderSoft}`, fontSize: '13px' };
const BADGE_TEXT = { auto: '자동', calc: '자동계산', assign: '자동지정' };

// 자동 배지 — 값이 있든 없든 늘 그린다. 자동 원값(autoOrigin)과 칸 값이 다르면 「수정됨」.
const Badge = ({ kind, field, edited = false }) => (
  <span data-testid={`claim-badge-${field}`} data-kind={edited ? 'edited' : kind} style={edited ? editedBadge : autoBadge}>
    {edited ? '수정됨' : BADGE_TEXT[kind]}
  </span>
);

function Field({ label, kind, field, edited, children }) {
  return (
    <label style={fieldBox}>
      <span>{label}{kind && <Badge kind={kind} field={field} edited={edited} />}</span>
      {children}
    </label>
  );
}

// 2.0·1.0 클레임 목록 — 같은 행 꼴(번호 · 유형 · 분류 · 상세사유 · 수량 · 일자 · 1.0 원발주서·배송비 · 재출고 송장 · [불러오기]).
function ClaimList({ source, claims, reshipOf, loadedKey, onLoad }) {
  const meta = CLAIM_LIST[source];
  const items = claims.items || [];
  return (
    <div style={{ marginTop: '12px' }} data-testid={`claim-${source}-list`}>
      <h3 style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: 600 }}>{meta.title}</h3>
      {source === 'v2' && claims.types_unset && <p data-testid="claim-v2-types-unset" style={{ ...note, color: COLORS.warn }}>{TYPES_UNSET_NOTE}</p>}
      {claims.status === 'format_unknown' && (
        <p style={{ ...note, color: COLORS.warn }}>{`${meta.title} 표 형식을 알아보지 못했습니다(열: ${(claims.columns || []).join(', ')})`}</p>
      )}
      {claims.status === 'error' && (
        <p style={{ ...note, color: COLORS.dangerText }}>{`${meta.title} 조회 오류 — ${claims.message || claims.code || ''}`}</p>
      )}
      {items.length === 0 && claims.status === 'ok' && !reshipOf && <p data-testid={`claim-${source}-empty`} style={note}>{meta.empty}</p>}
      {items.map((item) => {
        const number = item[meta.key];
        const detail = [item.detail_cd, item.detail_name].filter(Boolean).join(' ');
        return (
          <div key={number} data-testid={meta.row} data-claim-no={number} style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 10px', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${COLORS.borderSoft}`, fontSize: '13px' }}>
            <strong>{number}</strong>
            <span>{claimKindText(item, source)}</span>
            {item.category_label && (
              <span data-testid="claim-category" data-category={item.category || ''} style={badge(CATEGORY_TONE[item.category] || 'gray')}>{item.category_label}</span>
            )}
            {detail && <span>{detail}</span>}
            <span>{item.qty != null ? `${item.qty}개` : ''}</span>
            <span style={{ color: COLORS.textSub }}>{item.occurred_date || ''} {item.remark || ''}</span>
            {source === 'v1' && item.order_status_label && <span style={{ color: COLORS.textSub }}>{`원발주서 ${item.order_status_label}`}</span>}
            {source === 'v1' && item.ship_fee != null && <span style={{ color: COLORS.textSub }}>{`클레임 배송비 ${fmtWon(item.ship_fee)}`}</span>}
            {item.reship_invoice_no && (
              <span data-testid={meta.reship} style={{ color: COLORS.textSub }}>
                {`재출고 송장 ${item.reship_invoice_no}${item.reship_invoice_count > 1 ? ` 외 ${item.reship_invoice_count - 1}건` : ''}`}
              </span>
            )}
            <span style={{ flex: 1 }} />
            {item.registered && <span style={badge('gray')}>등록됨 {item.registered_claim_no || ''}</span>}
            <Button variant="secondary" size="sm" disabled={!!item.registered || (!!number && loadedKey === number)}
              onClick={() => onLoad(item, source)}>{item.registered ? '등록됨' : '불러오기'}</Button>
          </div>
        );
      })}
    </div>
  );
}

export default function ClaimRegisterPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const idParam = searchParams.get('id');
  const bulkUploadParam = searchParams.get('bulk_upload');
  const bulkRowParam = searchParams.get('bulk_row');
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState('loading');            // loading | ready | forbidden | error
  const [loadError, setLoadError] = useState('');
  const [invoiceText, setInvoiceText] = useState('');
  const [invoiceError, setInvoiceError] = useState('');
  const [invoice, setInvoice] = useState('');                  // 조회한(보낼) 송장번호
  const [lookup, setLookup] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [autoOrigin, setAutoOrigin] = useState(() => autoOriginFrom(null));   // 조회로 채운 원값(「수정됨」 비교)
  const [rows, setRows] = useState([]);
  const [claimRef, setClaimRef] = useState(null);              // 불러온 2.0·1.0 클레임(저장 본문 칸) · 저장한 원장의 연결
  const [loadNotices, setLoadNotices] = useState([]);          // 불러온 클레임의 서버 안내 문구(② 위)
  const [photos, setPhotos] = useState([]);                    // 저장된 사진
  const [pending, setPending] = useState([]);                  // 아직 안 올린 사진 [{key, file, name, url, error}]
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [claim, setClaim] = useState(null);                    // 저장된 원장(열린 것)
  const [today, setToday] = useState({ items: [], scope: 'mine' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [dismissed, setDismissed] = useState([]);              // 무시한 AI 제안(사유 id)
  const [bulkNote, setBulkNote] = useState(null);              // 엑셀 일괄등록에서 가져온 행 {file, rowNo}
  const previewSeq = useRef(0);
  const loadedId = useRef(null);
  const loadedBulk = useRef('');

  const reasons = useMemo(() => (form && form.reasons) || [], [form]);
  const labels = (form && form.rule_labels) || null;
  const reason = reasons.find((r) => String(r.id) === String(draft.reason_id)) || null;
  const readOnly = !!claim && !claim.editable;
  const ship = lookup && lookup.found ? lookup.shipment || {} : null;
  const edited = (...keys) => isEdited(draft, autoOrigin, keys);
  const evidenceMin = reason ? reason.evidence_min || 0 : 0;
  const photoCount = photos.length + pending.length;
  const set = useCallback((patch) => setDraft((d) => ({ ...d, ...patch })), []);

  // ── 불러오기 ──
  const loadToday = useCallback(async () => {
    try {
      const r = await claimLedgerAPI.today();
      setToday(r.data || { items: [] });
    } catch (e) { /* 오늘 목록은 보조 — 실패해도 화면은 그대로 */ }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await claimLedgerAPI.form();
        if (!alive) return;
        setForm(r.data || {});
        setStatus('ready');
      } catch (e) {
        if (!alive) return;
        if (statusOf(e) === 403) { setStatus('forbidden'); return; }
        setLoadError(errorText(e, '화면을 불러오지 못했습니다'));
        setStatus('error');
      }
    })();
    loadToday();
    return () => { alive = false; };
  }, [loadToday]);

  const applyClaim = useCallback((data) => {
    setClaim(data);
    setPhotos(data.photos || []);
    const snap = data.snapshot || null;
    setLookup(snap);
    setAutoOrigin(autoOriginFrom(snap));
    setInvoice(data.invoice_no || '');
    setInvoiceText(data.invoice_no || '');
    setRows(rowsFromClaim(data));
    const inputs = (data.calc_basis && data.calc_basis.inputs) || {};
    const li = inputs.logistics_inputs || {};
    setDraft({
      ...EMPTY_DRAFT,
      accident: data.accident_type || 'general',
      reason_id: data.reason_id ? String(data.reason_id) : '',
      extent: data.extent || '',
      fault: data.fault || '',
      handling: data.handling || '',
      temp_zone: data.temp_zone || '',
      center_key: data.center_key || '',
      shipper_id: data.shipper_id ? String(data.shipper_id) : '',
      shipper_name: data.shipper_id ? '' : (data.shipper_name || ''),
      ship_date: data.ship_date || '',
      delivered_date: data.delivered_date || '',
      occurred_date: data.occurred_date || '',
      memo: data.memo || '',
      reship_invoice_no: data.reship_invoice_no || '',
      intake_channel: data.intake_channel || '',
      compensation_method: data.compensation_method || '',
      pack_code: inputs.pack_code || '',
      restocked: !!inputs.restocked,
      manual_product_amount: inputs.manual_product_amount == null ? '' : String(inputs.manual_product_amount),
      manual_logistics_amount: inputs.manual_logistics_amount == null ? '' : String(inputs.manual_logistics_amount),
      logistics_inputs: Object.fromEntries(Object.entries(li).map(([k, v]) => [k, v == null ? '' : String(v)])),
    });
    setClaimRef(claimRefFromClaim(data));          // 2.0 · 1.0 클레임 · 재출고 원 클레임 연결을 되살린다
    setLoadNotices([]);
  }, []);

  // ?id= 로 열기 — 저장 직후 주소를 바꾼 경우(이미 그 원장)는 다시 읽지 않는다.
  useEffect(() => {
    if (!idParam || String(loadedId.current) === String(idParam)) return;
    let alive = true;
    (async () => {
      try {
        const r = await claimLedgerAPI.get(idParam);
        if (!alive) return;
        loadedId.current = r.data.id;
        applyClaim(r.data);
      } catch (e) {
        if (alive) toast(errorText(e, '클레임을 불러오지 못했습니다'), 'error');
      }
    })();
    return () => { alive = false; };
  }, [idParam, applyClaim]);

  // ?bulk_upload=&bulk_row= 로 열기(엑셀 일괄등록 오류 행) — 그 행 값으로 채운다. 조회(lookup)는 다시 부르지 않는다(저장한 스냅샷) · 원장 없음.
  useEffect(() => {
    if (!bulkUploadParam || !bulkRowParam) return undefined;
    const key = `${bulkUploadParam}/${bulkRowParam}`;
    if (loadedBulk.current === key) return undefined;
    let alive = true;
    (async () => {
      try {
        const r = await claimBulkAPI.row(bulkUploadParam, bulkRowParam);
        if (!alive) return;
        loadedBulk.current = key;
        const p = prefillFromBulkRow(r.data);
        loadedId.current = null;
        setClaim(null); setPhotos([]); setPending([]); setPreview(null); setSaveError(''); setDismissed([]);
        setLookup(p.lookup);
        setAutoOrigin(autoOriginFrom(p.lookup));
        setInvoice(p.invoice); setInvoiceText(p.invoiceText); setInvoiceError('');
        setRows(p.rows); setDraft(p.draft); setClaimRef(p.v2); setLoadNotices([]);
        setBulkNote({ file: '', rowNo: r.data ? r.data.row_no : null });
        try {
          const u = await claimBulkAPI.get(bulkUploadParam);       // 파일 이름(안내 문구에만 쓴다)
          if (alive) setBulkNote((n) => (n ? { ...n, file: (u.data && u.data.file_name) || '' } : n));
        } catch (e) { /* 안내에 파일 이름만 빠진다 */ }
      } catch (e) {
        if (alive) toast(errorText(e, '엑셀 일괄등록 행을 불러오지 못했습니다'), 'error');
      }
    })();
    return () => { alive = false; };
  }, [bulkUploadParam, bulkRowParam]);

  // ── 미리보기(300ms 디바운스 · 마지막 응답만) ──
  const claimId = claim ? claim.id : null;
  const previewBody = useMemo(() => ({ ...buildBody({ draft, rows, lookup, claimRef, invoice, photoCount }, 'draft'), claim_id: claimId }),
    [draft, rows, lookup, claimRef, invoice, photoCount, claimId]);
  const previewKey = JSON.stringify(previewBody);
  useEffect(() => {
    if (status !== 'ready' || readOnly) return undefined;
    const seq = previewSeq.current + 1;
    previewSeq.current = seq;
    const timer = setTimeout(async () => {
      try {
        const r = await claimLedgerAPI.preview(JSON.parse(previewKey));
        if (seq !== previewSeq.current) return;
        setPreview(r.data || null);
        setPreviewError('');
      } catch (e) {
        if (seq !== previewSeq.current) return;
        setPreviewError(errorText(e, '금액을 계산하지 못했습니다'));
      }
    }, PREVIEW_DELAY);
    return () => clearTimeout(timer);
  }, [previewKey, status, readOnly]);

  // ── ① 조회 ──
  const onLookup = async (text = invoiceText) => {
    const { value, error } = checkInvoice(text);
    setInvoiceError(error);
    if (error) return;
    setLookupBusy(true);
    try {
      const r = await claimLedgerAPI.lookup(value);
      const data = r.data || {};
      setInvoice(value);
      setLookup(data);
      setClaimRef(null);
      setLoadNotices([]);
      const origin = autoOriginFrom(data);
      setAutoOrigin(origin);
      if (data.found) {
        setRows(rowsFromLookup(data.items));
        setDraft((d) => ({ ...d, ...origin, temp_zone: origin.temp_zone || d.temp_zone }));
      } else {
        setRows((prev) => (prev.length ? prev : [blankRow()]));
      }
    } catch (e) {
      toast(errorText(e, '송장 조회에 실패했습니다'), 'error');
    } finally {
      setLookupBusy(false);
    }
  };
  // 재출고 송장 → 원주문 송장으로 다시 조회(송장 칸도 그 값으로 바꾼다).
  const lookupOriginal = (orig) => {
    setInvoiceText(orig);
    onLookup(orig);
  };

  // ── 줄 편집 ──
  const updateRow = (key, patch) => setRows((list) => list.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const checkedQty = (row) => (intOrNull(row.qty) > 0 ? row.qty : String(row.out_qty || 1));
  const toggleRow = (row) => {
    const selected = !row.selected;
    updateRow(row.key, { selected, qty: selected ? checkedQty(row) : row.qty });
  };
  // 묶음 머리 체크 — 그 주문 줄을 전부 선택(해제된 줄은 출고수량으로) · 이미 전부 선택이면 전부 해제.
  const toggleOrder = (group) => {
    const keys = new Set(group.rows.map((r) => r.key));
    const all = group.rows.every((r) => r.selected);
    setRows((list) => list.map((r) => {
      if (!keys.has(r.key)) return r;
      if (all) return { ...r, selected: false };
      return r.selected ? r : { ...r, selected: true, qty: checkedQty(r) };
    }));
  };
  // 2.0·1.0 클레임 [불러오기] — 서버 lines 의 줄만 체크 · 처리 방식·귀책·사유 안내 · 다른 클레임을 불러오면 바꾼다.
  const loadClaim = (item, source) => {
    const out = applyLoadedClaim(item, source, rows);
    if (!out) return;
    setRows(out.rows);
    set(out.draft);
    setClaimRef(out.claimRef);
    setLoadNotices(out.notices);
    const number = source === 'v1' ? item.v1_claim_key : item.v2_claim_no;
    toast(`${SOURCE_BADGE[source] || source} 클레임 ${number} 을(를) 불러왔습니다`);
  };

  // ── 사유 ──
  const pickReason = (id) => {
    const r = reasons.find((x) => String(x.id) === String(id));
    set({
      reason_id: id ? String(id) : '',
      fault: r ? (r.default_fault || '') : '',
      extent: r && r.accident_type === 'general' && r.comp_scope === 'select' ? draft.extent : '',
      ...(r ? { accident: r.accident_type } : {}),
    });
  };
  const applySuggestion = (s) => {
    const r = reasons.find((x) => x.id === s.reason_id);
    if (!r) return;
    pickReason(r.id);
  };

  // ── 사진 ──
  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const canUrl = typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function';
    setPending((list) => [...list, ...files.map((file, i) => ({
      key: `p-${Date.now()}-${i}-${file.name}`, file, name: file.name, url: canUrl ? URL.createObjectURL(file) : '', error: '',
    }))]);
  };
  const dropPending = (key) => setPending((list) => list.filter((p) => p.key !== key));
  const removePhoto = async (photo) => {
    if (!claim) return;
    try {
      await claimLedgerAPI.removePhoto(claim.id, photo.id);
      setPhotos((list) => list.filter((p) => p.id !== photo.id));
    } catch (e) {
      toast(errorText(e, '사진을 지우지 못했습니다'), 'error');
    }
  };

  // ── 저장 ──
  const resetAll = () => {
    loadedId.current = null;
    loadedBulk.current = '';
    setBulkNote(null);
    setClaim(null); setLookup(null); setInvoice(''); setInvoiceText(''); setRows([]); setClaimRef(null); setLoadNotices([]); setPhotos([]);
    setPending([]); setDraft(EMPTY_DRAFT); setAutoOrigin(autoOriginFrom(null)); setPreview(null); setSaveError(''); setDismissed([]);
  };

  const save = async (action) => {
    if (saving) return;
    setSaving(true);
    setSaveError('');
    try {
      const body = buildBody({ draft, rows, lookup, claimRef, invoice, photoCount }, action);
      delete body.photo_count;
      const r = claim ? await claimLedgerAPI.update(claim.id, body) : await claimLedgerAPI.create(body);
      const saved = r.data;
      loadedId.current = saved.id;
      setSearchParams({ id: String(saved.id) }, { replace: true });
      const failed = [];
      for (const p of pending) {
        try {
          await claimLedgerAPI.uploadPhoto(saved.id, p.file);      // 한 장씩(순서대로) — 실패한 사진만 남긴다
        } catch (e) {
          failed.push({ ...p, error: errorText(e, '사진을 올리지 못했습니다') });
        }
      }
      setPending(failed);
      let fresh = saved;
      try {
        fresh = (await claimLedgerAPI.get(saved.id)).data || saved;
      } catch (e) { /* 저장은 됐다 — 응답 값으로 그린다 */ }
      applyClaim(fresh);
      toast(action === 'draft' ? '임시저장했습니다' : `등록했습니다 — ${fresh.claim_no || ''} ${STATUS_LABEL[fresh.status] || ''}`.trim());
      if (failed.length) toast(`사진 ${failed.length}장을 올리지 못했습니다`, 'error');
      loadToday();
    } catch (e) {
      const msg = errorText(e, '저장하지 못했습니다');
      setSaveError(msg);
      toast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const discard = async () => {
    if (!claim) return;
    if (!(await confirmDialog('임시저장한 클레임을 지웁니다. 올린 사진도 함께 지워집니다.', { danger: true }))) return;
    try {
      await claimLedgerAPI.discard(claim.id);
      resetAll();
      setSearchParams({}, { replace: true });
      toast('임시저장을 지웠습니다');
      loadToday();
    } catch (e) {
      toast(errorText(e, '지우지 못했습니다'), 'error');
    }
  };

  const openToday = (item) => {
    loadedId.current = null;
    setSearchParams({ id: String(item.id) });
  };

  // ── 표시 값 ──
  const calc = preview && preview.calc;
  const deadlines = preview && preview.deadlines;
  const blockers = registerBlockers({ draft, rows, reason, preview });
  const reasonsForAccident = reasons.filter((r) => r.accident_type === draft.accident);
  const centers = (form && form.centers) || [];
  const shippers = ((form && form.shippers) || []).filter((s) => !draft.center_key || s.center_key === draft.center_key);
  const packItems = (form && form.pack_items) || [];
  const intakeOptions = optionsWithCurrent(form && form.intake_channel_options, draft.intake_channel);
  const compensationOptions = optionsWithCurrent(form && form.compensation_method_options, draft.compensation_method);
  const suggestions = ((preview && preview.suggestions) || []).filter((s) => !dismissed.includes(s.reason_id));
  const checks = (preview && preview.checks) || [];
  const approval = preview && preview.approval;
  const v2Claims = lookup && lookup.found ? lookup.v2_claims : null;
  const v1Claims = lookup && lookup.found ? lookup.v1_claims : null;
  const reshipOf = lookup && lookup.found ? lookup.reship_of || null : null;
  const loadedClaimKey = !!(claimRef && (claimRef.v2_claim_no || claimRef.v1_claim_key));
  const linkedText = claimRefText(claimRef);
  const packSummary = ship ? packSummaryText(ship) : '';
  const groups = groupRowsByOrder(rows);
  const grouped = groups.filter((g) => g.od_sno).length > 1;        // 주문이 2건 이상이면 주문별로 묶는다
  const rowIndex = new Map(rows.map((r, i) => [r.key, i]));         // 본문 items 순서(서버 items.N 필드)
  const missingText = amountMissingText(calc);
  const evidenceShort = photoCount < evidenceMin;
  const manualFields = new Set(((calc && calc.manual_required) || []).map((m) => m.field));
  const zoneSource = ship && ship.temp_zone_source ? ZONE_SOURCE[ship.temp_zone_source] || ship.temp_zone_source : '';

  // 상품 줄 한 행 — index 는 본문 items 순서(묶어 그려도 서버 items.N 과 맞춘다).
  const renderItemRow = (row) => {
    const index = rowIndex.get(row.key) ?? 0;
    return (
      <tr key={row.key} data-testid="claim-item-row" data-selected={row.selected ? 'true' : 'false'} data-od-sno={row.od_sno || ''}
        data-line-key={row.line_key || ''}>
        <td style={td}>
          <input type="checkbox" aria-label={`${row.prod_name || `${index + 1}번 줄`} 선택`} checked={row.selected} onChange={() => toggleRow(row)} />
        </td>
        <td style={td}>
          {row.manual
            ? <input aria-label="상품명" style={{ ...smallInput, width: '160px' }} value={row.prod_name} onChange={(e) => updateRow(row.key, { prod_name: e.target.value })} />
            : row.prod_name}
        </td>
        <td style={td}>
          {row.manual
            ? <input aria-label="상품코드" style={smallInput} value={row.prod_cd} onChange={(e) => updateRow(row.key, { prod_cd: e.target.value })} />
            : row.prod_cd}
        </td>
        <td style={td}>{row.manual ? '—' : row.out_qty}</td>
        <td style={td}>
          <input aria-label="클레임 수량" style={{ ...smallInput, width: '64px' }} value={row.qty} inputMode="numeric"
            onChange={(e) => updateRow(row.key, { qty: e.target.value })} />
        </td>
        <td style={td}>
          {row.manual
            ? <input aria-label="판매단가" style={smallInput} value={row.sale_price} onChange={(e) => updateRow(row.key, { sale_price: e.target.value })} />
            : fmtWon(row.sale_price)}
        </td>
        <td style={td}>
          {row.manual
            ? <input aria-label="결제금액" style={smallInput} value={row.pay_amount} onChange={(e) => updateRow(row.key, { pay_amount: e.target.value })} />
            : fmtWon(row.pay_amount)}
        </td>
        <td style={td}>
          {row.manual
            ? <input aria-label="매입가" style={smallInput} value={row.pur_price} onChange={(e) => updateRow(row.key, { pur_price: e.target.value })} />
            : (row.pur_price ? fmtWon(row.pur_price) : (
              <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                <span style={badge('warn')}>매입가 없음</span>
                <input aria-label="단가 직접 입력" style={smallInput} value={row.unit_price_input}
                  onChange={(e) => updateRow(row.key, { unit_price_input: e.target.value })} />
              </span>
            ))}
          {manualFields.has(`items.${index}.unit_price_input`) && row.manual && (
            <input aria-label="단가 직접 입력" style={{ ...smallInput, marginTop: '4px' }} value={row.unit_price_input}
              onChange={(e) => updateRow(row.key, { unit_price_input: e.target.value })} />
          )}
        </td>
      </tr>
    );
  };

  let subtitle = '송장번호로 출고를 찾아 클레임을 등록합니다';
  if (claim) subtitle = `${claim.claim_no || '임시저장'} · ${STATUS_LABEL[claim.status] || claim.status}${readOnly ? ' · 읽기 전용' : ''}`;

  return (
    <AppShell app="claim-register">
      <main style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto', background: `var(--gw-bg, ${COLORS.bg})` }}>
        <Page>
          <PageHeader icon={IconFileInvoice} tone="red" title="클레임 등록" subtitle={subtitle}
            actions={claim ? (
              <>
                <span data-testid="claim-status" style={badge(STATUS_TONE[claim.status] || 'gray')}>{STATUS_LABEL[claim.status] || claim.status}</span>
                <Button variant="secondary" onClick={() => { resetAll(); setSearchParams({}, { replace: true }); }}>새 클레임</Button>
              </>
            ) : null} />
          {status === 'loading' && <div role="status" style={{ color: COLORS.textMuted, fontSize: '12px' }}>불러오는 중…</div>}
          {status === 'forbidden' && <div role="alert" style={{ ...errText, fontSize: '13px' }}>이 화면을 볼 권한이 없습니다.</div>}
          {status === 'error' && <div role="alert" style={{ ...errText, fontSize: '13px' }}>{loadError}</div>}
          {bulkNote && bulkUploadParam && bulkRowParam && (
            <div role="status" data-testid="claim-bulk-prefill-note" style={{ ...badge('yellow'), whiteSpace: 'normal', fontSize: '13px', marginTop: '6px' }}>
              {['엑셀 일괄등록', bulkNote.file, `${bulkNote.rowNo ?? ''}행에서 가져왔습니다`].filter(Boolean).join(' ')}
            </div>
          )}
          {status === 'ready' && (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 16px', alignItems: 'flex-start' }}>
                <fieldset disabled={readOnly} style={{ flex: '3 1 620px', minWidth: 0, border: 0, padding: 0, margin: 0 }}>
                  {/* ① 송장번호로 주문 조회 */}
                  <div role="region" aria-label="① 송장번호로 주문 조회" style={sectionCard}>
                    <h2 style={sectionTitle}>① 송장번호로 주문 조회</h2>
                    <div style={grid}>
                      <label style={{ ...fieldBox, flex: '2 1 240px' }}>
                        <span>송장번호</span>
                        <input aria-label="송장번호" style={input} value={invoiceText} inputMode="numeric"
                          onChange={(e) => { setInvoiceText(e.target.value); setInvoiceError(''); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') onLookup(); }} />
                      </label>
                      <Button onClick={() => onLookup()} disabled={lookupBusy} style={{ marginBottom: '1px' }}>{lookupBusy ? '조회 중…' : '조회'}</Button>
                      {lookup && lookup.found && (
                        <span data-testid="claim-source" style={badge(lookup.source === 'v2' ? 'blue' : 'purple')}>
                          출처 {SOURCE_BADGE[lookup.source] || lookup.source}
                        </span>
                      )}
                    </div>
                    {invoiceError && <div role="alert" data-testid="claim-invoice-error" style={{ ...errText, marginTop: '6px' }}>{invoiceError}</div>}
                    {lookup && !lookup.found && (
                      <div data-testid="claim-no-shipment" style={{ ...note, color: COLORS.warn, fontWeight: 600 }}>{NO_SHIPMENT}</div>
                    )}
                    {lookup && (lookup.sources || []).filter((s) => s.status === 'error').map((s) => (
                      <div key={s.source} style={{ ...note, color: COLORS.dangerText }}>{`${SOURCE_BADGE[s.source] || s.source} 조회 오류 — ${s.message || s.code}`}</div>
                    ))}
                    {ship && ship.center_unknown && (
                      <div data-testid="claim-center-unknown" style={{ ...note, color: COLORS.warn, fontWeight: 600 }}>
                        {`센터 대장에 없는 창고코드 ${ship.depot_cd || ''} — 센터를 고르세요`}
                      </div>
                    )}
                    {reshipOf && (
                      <div data-testid="claim-reship-of" style={{ ...card, padding: '10px 12px', marginTop: '10px', borderColor: COLORS.primaryBorder, background: COLORS.primarySoft, fontSize: '13px' }}>
                        <div style={{ fontWeight: 600 }}>{reshipOf.message}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 10px', alignItems: 'center', marginTop: '6px' }}>
                          {reshipOf.orig_invoice_no && (
                            <Button variant="secondary" size="sm" disabled={lookupBusy} onClick={() => lookupOriginal(reshipOf.orig_invoice_no)}>
                              원주문 송장으로 조회
                            </Button>
                          )}
                          {!loadedClaimKey && (
                            <span style={{ fontSize: '12px', color: COLORS.textSub }}>{`이 송장으로 등록하면 원 클레임 ${reshipOf.claim_no}의 재출고 건으로 남습니다`}</span>
                          )}
                        </div>
                      </div>
                    )}
                    <div style={{ ...grid, marginTop: '12px' }}>
                      <Field label="센터" kind="auto" field="center" edited={edited('center_key')}>
                        <select aria-label="센터" style={input} value={draft.center_key}
                          onChange={(e) => set({ center_key: e.target.value, shipper_id: '' })}>
                          <option value="">센터 선택</option>
                          {centers.map((c) => <option key={c.center_key} value={c.center_key}>{c.display_name}</option>)}
                        </select>
                      </Field>
                      <Field label="고객사" kind="auto" field="shipper" edited={edited('shipper_id', 'shipper_name')}>
                        <select aria-label="고객사" style={input} value={draft.shipper_id}
                          onChange={(e) => set({ shipper_id: e.target.value, shipper_name: '' })}>
                          <option value="">{draft.shipper_name ? '명부에 없음(직접 입력)' : '고객사 선택'}</option>
                          {shippers.map((s) => <option key={s.id} value={s.id}>{s.display_name}</option>)}
                        </select>
                      </Field>
                      {!draft.shipper_id && (
                        <Field label="고객사 직접 입력">
                          <input aria-label="고객사 직접 입력" style={input} value={draft.shipper_name}
                            onChange={(e) => set({ shipper_name: e.target.value })} />
                        </Field>
                      )}
                      <Field label="출고일" kind="auto" field="ship-date" edited={edited('ship_date')}>
                        <input aria-label="출고일" type="date" style={input} value={draft.ship_date} onChange={(e) => set({ ship_date: e.target.value })} />
                      </Field>
                      <Field label="배송완료일" kind="auto" field="delivered-date" edited={edited('delivered_date')}>
                        <input aria-label="배송완료일" type="date" style={input} value={draft.delivered_date} onChange={(e) => set({ delivered_date: e.target.value })} />
                      </Field>
                      <Field label="품온" kind="auto" field="temp-zone" edited={edited('temp_zone')}>
                        <select aria-label="품온" style={input} value={draft.temp_zone} onChange={(e) => set({ temp_zone: e.target.value })}>
                          <option value="">품온 선택</option>
                          {ZONE_OPTIONS.map((z) => <option key={z.key} value={z.key}>{z.label}</option>)}
                        </select>
                        {zoneSource && <span style={{ fontSize: '11px' }}>근거: {zoneSource}</span>}
                      </Field>
                    </div>
                    <p data-testid="claim-courier" style={note}>
                      택배사<Badge kind="auto" field="courier" /> <strong>{(ship && (ship.courier_name || ship.courier_code)) || '—'}</strong>
                      {ship && (
                        <>
                          {' · '}
                          {ship.courier_group ? `그룹 ${ship.courier_group}` : <span style={{ color: COLORS.warn }}>택배사 그룹 미확정</span>}
                          {ship.order_no ? ` · 주문번호 ${ship.order_no}` : ''}
                        </>
                      )}
                    </p>
                    {/* 상품 표 — 주문이 2건 이상이면 주문별로 묶는다 */}
                    {packSummary && (
                      <p data-testid="claim-pack-summary" style={{ ...note, marginTop: '10px', fontWeight: 600, color: COLORS.text }}>{packSummary}</p>
                    )}
                    <div style={{ overflowX: 'auto', marginTop: '10px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={th}>선택</th>
                            <th style={th}>상품명<Badge kind="auto" field="prod-name" edited={columnEdited(rows, 'prod_name')} /></th>
                            <th style={th}>상품코드<Badge kind="auto" field="prod-cd" edited={columnEdited(rows, 'prod_cd')} /></th>
                            <th style={th}>출고수량</th><th style={th}>클레임 수량</th>
                            <th style={th}>판매단가<Badge kind="auto" field="sale-price" edited={columnEdited(rows, 'sale_price')} /></th>
                            <th style={th}>결제금액<Badge kind="auto" field="pay-amount" edited={columnEdited(rows, 'pay_amount')} /></th>
                            <th style={th}>매입가</th>
                          </tr>
                        </thead>
                        <tbody>
                          {grouped ? groups.map((group) => (
                            <Fragment key={`group-${group.od_sno || 'rest'}`}>
                              {group.od_sno ? (
                                <tr data-testid="claim-order-group" data-od-sno={group.od_sno}>
                                  <td style={{ ...td, background: COLORS.primarySoft }}>
                                    <input type="checkbox" aria-label={`주문 ${group.od_sno} 전체 선택`}
                                      checked={group.rows.every((r) => r.selected)} onChange={() => toggleOrder(group)} />
                                  </td>
                                  <td colSpan={7} style={{ ...td, background: COLORS.primarySoft, fontWeight: 600 }}>{`주문 ${group.od_sno} · ${group.rows.length}줄`}</td>
                                </tr>
                              ) : (
                                <tr data-testid="claim-rest-group">
                                  <td colSpan={8} style={{ ...td, background: COLORS.primarySoft, fontWeight: 600 }}>
                                    {`${group.rows.every((r) => r.manual) ? '직접 입력' : '주문번호 없음'} · ${group.rows.length}줄`}
                                  </td>
                                </tr>
                              )}
                              {group.rows.map(renderItemRow)}
                            </Fragment>
                          )) : rows.map(renderItemRow)}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <Button variant="secondary" size="sm" onClick={() => setRows((list) => [...list, blankRow()])}>+ 상품 추가</Button>
                      {lookup && lookup.v1_qty_assumed && <span data-testid="claim-v1-note" style={note}>{V1_QTY_NOTE}</span>}
                    </div>
                    {v2Claims && (
                      <ClaimList source="v2" claims={v2Claims} reshipOf={reshipOf} loadedKey={claimRef ? claimRef.v2_claim_no : null} onLoad={loadClaim} />
                    )}
                    {v1Claims && (
                      <ClaimList source="v1" claims={v1Claims} reshipOf={reshipOf} loadedKey={claimRef ? claimRef.v1_claim_key : null} onLoad={loadClaim} />
                    )}
                    {linkedText && <p data-testid="claim-linked-ref" style={note}>{linkedText}</p>}
                  </div>

                  {loadNotices.length > 0 && (
                    <div role="status" data-testid="claim-load-notice" style={{ ...card, marginTop: '14px', padding: '10px 12px', borderColor: COLORS.warnSoft, background: COLORS.warnSoft, color: COLORS.warn, fontSize: '13px' }}>
                      {loadNotices.map((text) => <div key={text}>{text}</div>)}
                    </div>
                  )}

                  {/* ② 클레임 내용 입력 */}
                  <div role="region" aria-label="② 클레임 내용 입력" style={sectionCard}>
                    <h2 style={sectionTitle}>② 클레임 내용 입력</h2>
                    <div role="radiogroup" aria-label="사고 구분" style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                      {ACCIDENTS.map((a) => (
                        <button key={a.key} type="button" role="radio" aria-checked={draft.accident === a.key}
                          onClick={() => set({ accident: a.key, reason_id: '', fault: '', extent: '' })}
                          style={{ ...badge(draft.accident === a.key ? 'blue' : 'gray'), border: 0, cursor: 'pointer', fontSize: '13px', padding: '5px 12px' }}>
                          {a.label}
                        </button>
                      ))}
                    </div>
                    <div style={grid}>
                      <Field label="클레임 사유">
                        <select aria-label="클레임 사유" style={input} value={draft.reason_id} onChange={(e) => pickReason(e.target.value)}>
                          <option value="">사유 선택</option>
                          {reasonsForAccident.map((r) => <option key={r.id} value={r.id}>{`${r.code} ${r.name}`}</option>)}
                        </select>
                      </Field>
                      {reason && reason.accident_type === 'general' && reason.comp_scope === 'select' && (
                        <Field label="범위">
                          <select aria-label="범위" style={input} value={draft.extent} onChange={(e) => set({ extent: e.target.value })}>
                            <option value="">범위 선택</option>
                            <option value="partial">일부</option>
                            <option value="all">전제품</option>
                          </select>
                        </Field>
                      )}
                      <Field label="귀책">
                        <select aria-label="귀책" style={input} value={draft.fault} onChange={(e) => set({ fault: e.target.value })}>
                          <option value="">귀책 선택</option>
                          {FAULT_OPTIONS.map((f) => <option key={f} value={f}>{labelOf('fault', f, labels)}</option>)}
                        </select>
                      </Field>
                      <Field label="처리 방식">
                        <select aria-label="처리 방식" style={input} value={draft.handling} onChange={(e) => set({ handling: e.target.value })}>
                          <option value="">처리 방식 선택</option>
                          {HANDLING_OPTIONS.map((h) => <option key={h} value={h}>{labelOf('handling', h, labels)}</option>)}
                        </select>
                      </Field>
                      <Field label="재출고 송장">
                        <input aria-label="재출고 송장" style={input} value={draft.reship_invoice_no} onChange={(e) => set({ reship_invoice_no: e.target.value })} />
                      </Field>
                      <Field label="배상 방식">
                        <select aria-label="배상 방식" style={input} value={draft.compensation_method}
                          onChange={(e) => set({ compensation_method: e.target.value })}>
                          <option value="">선택 안 함</option>
                          {compensationOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </Field>
                      <Field label="발생일자">
                        <input aria-label="발생일자" type="date" style={input} value={draft.occurred_date} onChange={(e) => set({ occurred_date: e.target.value })} />
                      </Field>
                      <Field label="접수 경로">
                        <select aria-label="접수 경로" style={input} value={draft.intake_channel}
                          onChange={(e) => set({ intake_channel: e.target.value })}>
                          <option value="">선택 안 함</option>
                          {intakeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </Field>
                    </div>
                    {reason && reason.note && <p style={note}>{reason.note}</p>}
                    <label style={{ ...fieldBox, marginTop: '10px' }}>
                      <span>발생 경위/메모</span>
                      <textarea aria-label="발생 경위/메모" rows={3} style={{ ...input, resize: 'vertical' }} value={draft.memo}
                        onChange={(e) => set({ memo: e.target.value })} />
                    </label>
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '12px', color: COLORS.textSub }}>
                        <span>증빙사진</span>
                        <span data-testid="claim-evidence-count">{`${photoCount}/최소 ${evidenceMin}`}</span>
                        {evidenceShort && <span data-testid="claim-evidence-badge" style={badge('warn')}>증빙 미비</span>}
                        {reason && reason.evidence_guide && <span>({reason.evidence_guide})</span>}
                        {!readOnly && (
                          <label style={{ ...badge('blue'), cursor: 'pointer' }}>
                            + 사진
                            <input aria-label="증빙사진 추가" type="file" accept="image/*" multiple style={{ display: 'none' }}
                              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
                          </label>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                        {photos.map((p) => (
                          <span key={`s-${p.id}`} data-testid="claim-photo" style={{ ...badge('gray'), gap: '6px' }}>
                            {p.original_name || `사진 ${p.id}`}
                            {!readOnly && claim && (claim.editable || claim.status !== 'draft') && (
                              <button type="button" aria-label={`${p.original_name || '사진'} 지우기`} onClick={() => removePhoto(p)}
                                style={{ border: 0, background: 'transparent', cursor: 'pointer', color: COLORS.textSub }}>×</button>
                            )}
                          </span>
                        ))}
                        {pending.map((p) => (
                          <span key={p.key} data-testid="claim-photo-pending" style={{ ...badge(p.error ? 'danger' : 'yellow'), gap: '6px' }}>
                            {p.url && <img src={p.url} alt="" style={{ width: '22px', height: '22px', objectFit: 'cover', borderRadius: '3px' }} />}
                            {p.name}{p.error ? ` — ${p.error}` : ' (저장할 때 올립니다)'}
                            <button type="button" aria-label={`${p.name} 빼기`} onClick={() => dropPending(p.key)}
                              style={{ border: 0, background: 'transparent', cursor: 'pointer' }}>×</button>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ③ 금액 · 접수기한 */}
                  <div role="region" aria-label="③ 금액 · 접수기한" style={sectionCard}>
                    <h2 style={sectionTitle}>③ 금액 · 접수기한</h2>
                    {calc && calc.blocked && (
                      <div role="alert" data-testid="claim-blocked" style={{ ...card, padding: '10px 12px', borderColor: COLORS.danger, background: COLORS.dangerSoft, color: COLORS.dangerText, fontSize: '13px', marginBottom: '10px' }}>
                        {calc.blocked.message}
                      </div>
                    )}
                    {previewError && <div style={{ ...errText, marginBottom: '8px' }}>{previewError}</div>}
                    {calc && calc.per_case && (
                      <div style={{ ...grid, marginBottom: '8px' }}>
                        <Field label="제품가 직접 입력(건별 판정)">
                          <input aria-label="제품가 직접 입력" style={input} value={draft.manual_product_amount}
                            onChange={(e) => set({ manual_product_amount: e.target.value })} />
                        </Field>
                        <Field label="물류비 직접 입력(건별 판정)">
                          <input aria-label="물류비 직접 입력" style={input} value={draft.manual_logistics_amount}
                            onChange={(e) => set({ manual_logistics_amount: e.target.value })} />
                        </Field>
                      </div>
                    )}
                    <div style={amountRow} data-testid="claim-product-amount" data-disabled={calc && calc.product_comp === 'X' ? 'true' : 'false'}>
                      <span>제품가 <span style={{ color: COLORS.textSub, fontSize: '12px' }}>{productBasisText(calc)}</span></span>
                      <strong style={{ color: calc && calc.product_comp === 'X' ? COLORS.textMuted : COLORS.text }}>{fmtWon(calc ? calc.product_amount : null)}</strong>
                    </div>
                    {calc && calc.product_comp === '△' && (
                      <label style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', fontSize: '13px', marginTop: '6px' }}>
                        <input type="checkbox" checked={!!draft.restocked} onChange={(e) => set({ restocked: e.target.checked })} />
                        회수 후 재고복구됨(제품가 미변상)
                      </label>
                    )}
                    <div style={{ ...amountRow, flexDirection: 'column', alignItems: 'stretch' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>물류비{calc && calc.logistics_comp === 'X' ? ' (미변상)' : ''}</span>
                        <strong>{fmtWon(calc ? calc.logistics_amount : null)}</strong>
                      </div>
                      {calc && (calc.components || []).map((c) => (
                        <div key={c.key} style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '12px', color: COLORS.textSub, marginTop: '4px' }}>
                          <span style={{ minWidth: '90px' }}>{c.label}</span>
                          {c.key === 'pack_fee' && packItems.length > 0 && (
                            <select aria-label="부자재 단가" style={{ ...smallInput, width: '120px' }} value={draft.pack_code || c.code || ''}
                              onChange={(e) => set({ pack_code: e.target.value })}>
                              {packItems.map((p) => <option key={p.code} value={p.code}>{`${p.name} ${fmtWon(p.unit_price)}`}</option>)}
                            </select>
                          )}
                          <input aria-label={`${c.label} 금액`} style={smallInput} placeholder={String(c.amount)}
                            value={(draft.logistics_inputs || {})[c.key] || ''}
                            onChange={(e) => set({ logistics_inputs: { ...(draft.logistics_inputs || {}), [c.key]: e.target.value } })} />
                          <span>{c.source === 'wms' ? 'WMS 값' : c.source === 'input' ? '직접 입력' : c.source === 'cost_item' ? '단가표' : '단가 없음'}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ ...amountRow, fontSize: '15px' }} data-testid="claim-total" data-total={calc && calc.total_amount != null ? String(calc.total_amount) : ''}
                      data-total-vat={calc && calc.total_amount_vat != null ? String(calc.total_amount_vat) : ''}>
                      <span>
                        클레임 금액<Badge kind="calc" field="total" />
                        {calc && calc.exempt && (
                          <span data-testid="claim-exempt" style={{ ...badge('orange'), fontSize: '11px', padding: '1px 6px', marginLeft: '6px' }}>고객사·면책 — 금액 0원</span>
                        )}
                      </span>
                      <strong>{`VAT 별도 ${fmtWon(calc ? calc.total_amount : null)} · VAT 포함 ${fmtWon(calc ? calc.total_amount_vat : null)}`}</strong>
                    </div>
                    {calc && calc.total_amount == null && missingText && (
                      <div data-testid="claim-amount-missing" style={{ ...note, color: COLORS.warn }}>{missingText}</div>
                    )}
                    <div style={amountRow} data-testid="claim-courier-comp">
                      <span>택배사 보상 예상<Badge kind="calc" field="courier-comp" /></span>
                      <span>
                        {calc && calc.courier_comp_expected != null
                          ? `${fmtWon(calc.courier_comp_expected)} (${calc.courier_comp_pct}% 적용${calc.box_limit_total != null ? ` · BOX당 한도 합계 ${fmtWon(calc.box_limit_total)}` : ''})`
                          : '—'}
                      </span>
                    </div>
                    <div style={amountRow} data-testid="claim-shipper-deadline">
                      <span>고객사 접수기한<Badge kind="calc" field="shipper-deadline" /></span>
                      <span style={{ color: deadlines && deadlines.shipper && deadlines.shipper.overdue ? COLORS.dangerText : COLORS.text }}>
                        {shipperDeadlineText(deadlines && deadlines.shipper)}
                      </span>
                    </div>
                    {deadlines && deadlines.shipper && deadlines.shipper.state === 'unset' && calc && (calc.missing || []).length > 0 && missingText && (
                      <div data-testid="claim-deadline-missing" style={{ ...note, color: COLORS.warn }}>{missingText}</div>
                    )}
                    <div style={amountRow} data-testid="claim-courier-deadline">
                      <span>택배사 접수기한<Badge kind="calc" field="courier-deadline" /></span>
                      <span>{courierDeadlineText(deadlines && deadlines.courier)}</span>
                    </div>
                    {calc && (calc.flags || []).length > 0 && (
                      <ul style={{ ...note, paddingLeft: '18px' }}>
                        {calc.flags.map((f) => <li key={f}>{f}</li>)}
                      </ul>
                    )}
                    {calc && (calc.manual_required || []).length > 0 && (
                      <ul style={{ ...note, paddingLeft: '18px', color: COLORS.warn }}>
                        {calc.manual_required.map((m) => <li key={m.field}>{m.message}</li>)}
                      </ul>
                    )}
                  </div>
                </fieldset>

                {/* 오른쪽 열 */}
                <div style={{ flex: '2 1 300px', minWidth: 0 }}>
                  <div role="region" aria-label="④ AI 자동 분류 제안" style={sectionCard}>
                    <h2 style={sectionTitle}>④ AI 자동 분류 제안</h2>
                    {suggestions.length === 0 && <p style={note}>메모나 2.0·1.0 사유로 고를 만한 사유가 없습니다.</p>}
                    {suggestions.map((s) => (
                      <div key={s.reason_id} data-testid="claim-suggestion" style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '6px 0', fontSize: '13px' }}>
                        <span style={{ flex: 1 }}>{`${s.code} ${s.name}`} <span style={{ color: COLORS.textSub, fontSize: '12px' }}>{s.basis === 'v2_link' ? '2.0 사유 연결' : s.basis === 'v1_link' ? '1.0 사유 연결' : `키워드 ${(s.matched || []).join(', ')}`}</span></span>
                        <Button size="sm" disabled={readOnly} onClick={() => applySuggestion(s)}>제안 적용</Button>
                        <Button size="sm" variant="secondary" onClick={() => setDismissed((d) => [...d, s.reason_id])}>무시</Button>
                      </div>
                    ))}
                  </div>
                  <div role="region" aria-label="⑤ 접수 기준 체크" style={sectionCard}>
                    <h2 style={sectionTitle}>⑤ 접수 기준 체크</h2>
                    {checks.length === 0 && <p style={note}>입력하면 접수 기준을 확인합니다.</p>}
                    {checks.map((c) => (
                      <div key={c.key} data-testid="claim-check-row" data-ok={c.ok ? 'true' : 'false'}
                        style={{ display: 'flex', gap: '8px', padding: '5px 0', fontSize: '13px', color: c.level === 'block' ? COLORS.dangerText : c.ok ? COLORS.text : COLORS.warn }}>
                        <span aria-hidden="true">{c.ok ? '✓' : '!'}</span>
                        <span>{c.label}{c.detail ? ` — ${c.detail}` : ''}</span>
                      </div>
                    ))}
                  </div>
                  <div role="region" aria-label="승인 담당자" style={sectionCard}>
                    <h2 style={sectionTitle}>승인 담당자<Badge kind="assign" field="approver" /></h2>
                    <div data-testid="claim-approval" style={{ fontSize: '13px' }}>
                      {claim && claim.status !== 'draft'
                        ? (claim.approver_name || (claim.status === 'registered' ? '승인 불필요' : '—'))
                        : !approval ? '—'
                          : !approval.needed ? '승인 불필요(센터 귀책 기준금액 미만)'
                            : approval.self_approve ? '본인 결재(센터장)'
                              : approval.approvers.length ? `${approval.approvers.join(', ')} (자동지정)` : '지정할 승인자가 없습니다'}
                      {approval && approval.missing_reason && <div style={{ ...note, color: COLORS.warn }}>{`승인자 대체 사유: ${approval.missing_reason}`}</div>}
                    </div>
                  </div>
                  {!readOnly && (
                    <div style={{ ...sectionCard, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <Button data-testid="claim-register" onClick={() => save('register')} disabled={saving || blockers.length > 0}>
                        {saving ? '저장 중…' : '등록(승인)'}
                      </Button>
                      {blockers.length > 0 && (
                        <div data-testid="claim-register-reason" style={{ ...note, margin: 0, color: COLORS.warn }}>{`등록할 수 없습니다 — ${blockers.join(' · ')}`}</div>
                      )}
                      <Button variant="secondary" data-testid="claim-draft" onClick={() => save('draft')} disabled={saving}>임시저장</Button>
                      {claim && claim.can_discard && (
                        <Button variant="danger" data-testid="claim-discard" onClick={discard} disabled={saving}>임시저장 삭제</Button>
                      )}
                      <Button variant="secondary" data-testid="claim-bulk-open" onClick={() => navigate('/claims/register/bulk')}>엑셀 일괄등록</Button>
                      {saveError && <div role="alert" style={errText}>{saveError}</div>}
                    </div>
                  )}
                </div>
              </div>

              {/* 오늘 등록한 클레임 */}
              <div role="region" aria-label="오늘 등록한 클레임" style={sectionCard}>
                <h2 style={sectionTitle}>오늘 등록한 클레임 <span style={{ fontSize: '12px', color: COLORS.textSub, fontWeight: 400 }}>{today.scope === 'center' ? '내 소속 센터' : '내가 만든 것'}</span></h2>
                {(today.items || []).length === 0 && <p style={note}>오늘 등록한 클레임이 없습니다.</p>}
                {(today.items || []).length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr><th style={th}>시간</th><th style={th}>번호</th><th style={th}>송장번호</th><th style={th}>고객사</th><th style={th}>사유</th><th style={th}>금액(VAT 포함)</th><th style={th}>상태</th></tr>
                    </thead>
                    <tbody>
                      {today.items.map((item) => (
                        <tr key={item.id} data-testid="claim-today-row" data-claim-id={item.id} onClick={() => openToday(item)} style={{ cursor: 'pointer' }}>
                          <td style={td}>{fmtKstShort(item.created_at)}</td>
                          <td style={td}>{item.claim_no || '임시저장'}</td>
                          <td style={td}>{item.invoice_no || '—'}</td>
                          <td style={td}>{item.shipper_name || '—'}</td>
                          <td style={td}>{item.reason_name || '—'}</td>
                          <td style={td}>{fmtWon(item.total_amount_vat)}</td>
                          <td style={td}>
                            <span style={badge(STATUS_TONE[item.status] || 'gray')}>{STATUS_LABEL[item.status] || item.status}</span>
                            {item.fault === 'shipper' && <span data-testid="claim-today-exempt" style={{ ...badge('orange'), marginLeft: '6px' }}>면책</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </Page>
      </main>
    </AppShell>
  );
}
